import { isEntityCluster } from "@/data-preparation-tool/utils/entity-utils";
import { sortByName } from "@/data-preparation-tool/utils/sorting";
import { RootState } from "@/store/store";
import { GUID } from "@faro-lotv/foundation";
import {
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
  CreateOrUpdateClusterEntityParams,
  CreateOrUpdateScanEntityParams,
  ProjectApi,
  RegistrationRevision,
} from "@faro-lotv/service-wires";
import { applyRevision } from "../../editing/apply-revision";
import { revisionToEdit } from "../../inspect-and-publish/refine-and-publish-utils";
import { isSpecialCaptureCluster } from "./scan-tree-drag-drop-utils";

/** The children of one entity in their new order. */
type SortedChildren = {
  /** The ID of the entity whose children are sorted. */
  parentId: GUID;
  /** The children of the entity, in their new order. */
  children: CaptureTreeEntityRevision[];
};

/**
 * @param entityA The first sibling to compare.
 * @param entityB The second sibling to compare.
 * @returns A negative number if entityA comes first, a positive number if entityB comes first.
 *  Clusters come before scans, entities of the same kind are sorted ascending by name.
 */
function compareSiblings(
  entityA: CaptureTreeEntityRevision,
  entityB: CaptureTreeEntityRevision,
): number {
  const isClusterA = isEntityCluster(entityA);
  const isClusterB = isEntityCluster(entityB);
  if (isClusterA !== isClusterB) {
    return isClusterA ? -1 : 1;
  }
  return sortByName(entityA, entityB);
}

/**
 * Sorts the descendants of an entity recursively: clusters first, scans second, each ascending
 * by name. The children of special capture clusters (Orbis, SiteScape) are left untouched, as
 * their contents can't be reordered by the user.
 *
 * @param entities All entities of the revision, in their current order.
 * @param entityId The ID of the entity whose descendants are sorted.
 * @returns The new order of the direct children, for every descendant whose children order
 *  changed, including the entity itself.
 */
export function collectSortedDescendants(
  entities: CaptureTreeEntityRevision[],
  entityId: GUID,
): SortedChildren[] {
  const childrenByParent = new Map<GUID, CaptureTreeEntityRevision[]>();
  for (const entity of entities) {
    if (!entity.parentId) {
      continue;
    }
    const siblings = childrenByParent.get(entity.parentId);
    if (siblings) {
      siblings.push(entity);
    } else {
      childrenByParent.set(entity.parentId, [entity]);
    }
  }

  const sortedDescendants: SortedChildren[] = [];

  function visit(parentId: GUID): void {
    const children = childrenByParent.get(parentId) ?? [];
    const sorted = [...children].sort(compareSiblings);

    // If the order of the children changed, record it.
    if (sorted.some((child, index) => child.id !== children[index].id)) {
      sortedDescendants.push({ parentId, children: sorted });
    }

    for (const child of sorted) {
      if (isEntityCluster(child) && !isSpecialCaptureCluster(child)) {
        visit(child.id);
      }
    }
  }
  visit(entityId);

  return sortedDescendants;
}

/**
 * @param entities All entities of the revision, in their current order.
 * @param sortedDescendants The new order of the children, for every sorted descendant whose
 *  children order changed.
 * @returns The entities in their new order, which defines the order of the siblings in the tree.
 */
export function applySortedDescendants(
  entities: CaptureTreeEntityRevision[],
  sortedDescendants: SortedChildren[],
): CaptureTreeEntityRevision[] {
  const result = [...entities];
  const slotOfEntity = new Map(
    entities.map((entity, index) => [entity.id, index]),
  );

  for (const { children } of sortedDescendants) {
    // The children are written back into the slots they currently occupy, in ascending order,
    // so that all other entities keep their position.
    const slots = children
      .map((child) => slotOfEntity.get(child.id))
      .filter((slot) => slot !== undefined)
      .sort((slotA, slotB) => slotA - slotB);

    slots.forEach((slot, index) => {
      result[slot] = children[index];
    });
  }

  return result;
}

type SortUpdates = {
  /** The updates for the sorted clusters. */
  clusters: CreateOrUpdateClusterEntityParams[];
  /** The updates for the sorted scans. */
  scans: CreateOrUpdateScanEntityParams[];
};

/**
 * @param sortedDescendants The new order of the children, for every sorted descendant whose
 *  children order changed.
 * @returns The updates to persist the new order, split by entity kind because clusters and scans
 *  need separate requests. Within each kind, the entities are ordered so that the backend, which
 *  applies the updates of a request one after another, ends up with the intended order.
 */
export function createSortUpdates(
  sortedDescendants: SortedChildren[],
): SortUpdates {
  const updates: SortUpdates = { clusters: [], scans: [] };

  for (const { children } of sortedDescendants) {
    // Each entity is placed before its successor, the last one is placed last. Moving an entity
    // only has the intended effect once its successor is in its final position, so the entities
    // are updated from the last to the first one.
    for (let index = children.length - 1; index >= 0; index--) {
      const child = children[index];
      const update = {
        id: child.id,
        beforeSibling: children[index + 1]?.id ?? null,
      };

      if (child.type === CaptureTreeEntityType.cluster) {
        updates.clusters.push(update);
      } else {
        updates.scans.push(update);
      }
    }
  }

  return updates;
}

type SaveSortedEntitiesParams = {
  /** The Project API client used to persist the changes. */
  projectApiClient: ProjectApi;
  /** The current application state. */
  state: RootState;
  /** The revision the sorted entities belong to. */
  revision: RegistrationRevision;
  /** The new order of the children, for every sorted descendant whose children order changed. */
  sortedDescendants: SortedChildren[];
};

/**
 * Persists the new order of the sorted entities in the Draft view's scan tree, using the same
 * approach as for moving entities in saveMovedEntities.
 *
 * @param params Parameters describing the new order.
 */
export async function saveSortedEntities({
  projectApiClient,
  state,
  revision,
  sortedDescendants,
}: SaveSortedEntitiesParams): Promise<void> {
  const { clusters, scans } = createSortUpdates(sortedDescendants);
  if (!clusters.length && !scans.length) {
    return;
  }

  const editRevision = await revisionToEdit(revision, projectApiClient, state);

  // The clusters are placed before the scans, so they must be updated first: the scans are then
  // moved to the end of their parent's children, after all clusters.
  if (clusters.length) {
    await projectApiClient.createOrUpdateClusterEntitiesForRegistrationRevision(
      { registrationRevisionId: editRevision.id, requestBody: clusters },
    );
  }
  if (scans.length) {
    await projectApiClient.createOrUpdateScanEntitiesForRegistrationRevision({
      registrationRevisionId: editRevision.id,
      requestBody: scans,
    });
  }

  await applyRevision({ revision: editRevision, projectApiClient });
}
